import { useEffect, useId, useRef, useState } from "react";
import { formatarData } from "../lib/dados";
import type { Resposta } from "../lib/adminCatalogo";
import type { MinhaResposta } from "../lib/pesquisas";
import { Recado } from "./ui";

/**
 * A nota de 0 a 10 e o comentário opcional — o formulário das DUAS portas de
 * uma pesquisa: a página Avaliações (a sessão do aluno) e a página do link que
 * chega por e-mail (000320). Uma resposta só por pessoa, venha ela por onde
 * vier, pede um formulário só: dois divergiriam na primeira mudança.
 *
 * Quem ENVIA é quem usa o formulário (`enviar`): a sessão chama
 * `responder_pesquisa`, o link chama `responder_pesquisa_por_convite`. O
 * formulário só desenha e mostra a frase que o banco devolveu.
 *
 * A escala é de RADIOS nativos, desenhados como botões: é o que dá ao teclado
 * as setas de graça e ao leitor de tela "1 de 11, selecionado" sem uma linha de
 * ARIA. O rótulo de cada extremo fica escrito embaixo, porque "0" e "10" sem a
 * âncora não dizem de que lado está o "recomendaria".
 */
export default function FormularioDeNota({
  chave,
  rotulo,
  atual,
  notaInicial = null,
  enviar,
  aoResponder,
}: {
  /** Distingue os grupos de rádio de vários formulários na mesma página. */
  chave: string;
  rotulo: string;
  atual: MinhaResposta | null;
  /**
   * A nota que o aluno escolheu no E-MAIL (`?nota=` da página do link). Ela só
   * MARCA — nada é gravado até ele confirmar, porque leitor de links segue todo
   * link de todo e-mail.
   */
  notaInicial?: number | null;
  enviar: (nota: number, comentario: string | null) => Promise<Resposta>;
  aoResponder: () => Promise<unknown>;
}) {
  const nomeDoGrupo = `nota-${chave}`;
  const idEscala = useId();
  const idComentario = useId();
  const [nota, setNota] = useState<number | null>(notaInicial ?? atual?.nota ?? null);
  const [comentario, setComentario] = useState(atual?.comentario ?? "");
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  // A resposta mudada fora daqui (a lista recarregada depois de enviar) passa a
  // ser o ponto de partida. Compara com a ANTERIOR, e não "pula a primeira vez":
  // em modo estrito o efeito roda duas vezes na montagem, e a segunda apagaria a
  // nota que o aluno escolheu no e-mail.
  const anterior = useRef({ nota: atual?.nota ?? null, comentario: atual?.comentario ?? "" });
  useEffect(() => {
    const n = atual?.nota ?? null;
    const c = atual?.comentario ?? "";
    if (anterior.current.nota === n && anterior.current.comentario === c) return;
    anterior.current = { nota: n, comentario: c };
    setNota(n);
    setComentario(c);
  }, [atual?.nota, atual?.comentario]);

  const mudou = nota !== (atual?.nota ?? null) || comentario.trim() !== (atual?.comentario ?? "");

  async function confirmar() {
    if (nota === null) return;
    setEnviando(true);
    setRecado(null);
    const r = await enviar(nota, comentario.trim() || null);
    setEnviando(false);
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: r.detalhe ?? (r.ok ? "resposta registrada." : "não foi possível enviar.") });
    if (r.ok) await aoResponder();
  }

  return (
    <div className="mt-3" data-formulario={chave}>
      <div role="radiogroup" aria-labelledby={idEscala} className="flex flex-wrap gap-1.5">
        <span id={idEscala} className="sr-only">
          Sua nota para {rotulo}, de 0 a 10
        </span>
        {Array.from({ length: 11 }, (_, n) => (
          <label key={n} className="relative">
            <input
              type="radio"
              name={nomeDoGrupo}
              value={n}
              checked={nota === n}
              onChange={() => setNota(n)}
              className="peer sr-only"
              data-nota={n}
            />
            <span className="grid size-10 cursor-pointer place-items-center rounded-lg border border-borda bg-superficie text-sm font-semibold text-tinta transition peer-checked:border-acento peer-checked:bg-acento peer-checked:text-acento-tinta peer-focus-visible:ring-2 peer-focus-visible:ring-acento hover:border-acento">
              {n}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-1 flex max-w-[31rem] justify-between text-xs text-cinza" aria-hidden="true">
        <span>0 — não recomendaria</span>
        <span>10 — recomendaria com certeza</span>
      </div>

      <label htmlFor={idComentario} className="mt-3 block text-sm font-medium text-tinta">
        Comentário <span className="font-normal text-cinza">(opcional)</span>
      </label>
      <textarea
        id={idComentario}
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        maxLength={2000}
        rows={2}
        className="mt-1 w-full rounded-xl border border-borda bg-superficie p-3 text-sm text-tinta"
        data-comentario
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void confirmar()}
          disabled={nota === null || enviando || !mudou}
          className={nota === null || !mudou ? "pill-desligada" : "pill-cta"}
          data-enviar
        >
          {enviando ? "Enviando…" : atual ? "Atualizar resposta" : "Enviar resposta"}
        </button>
        {atual && !mudou && (
          <span className="text-xs text-cinza" data-ja-respondida>
            Sua resposta: {atual.nota} · enviada em {formatarData(atual.atualizada_em)}
          </span>
        )}
      </div>
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </div>
  );
}
